import { ApiResponse, TicketManagerRootCauseGroup, TicketManagerRootCauseReason } from '@vision/ui/interfaces';
import { TicketManagerApi } from '../ticket-manager.api';
import {
  TicketManagerAddRootCauseToGroupArgs,
  TicketManagerProjectRootCauseGroupsArgs,
  TicketManagerProjectRootCausesArgs,
  TicketManagerRemoveRootCauseFromGroupArgs,
  TicketManagerRootCauseCreateArgs,
  TicketManagerRootCauseGroupCreateArgs,
  TicketManagerRootCauseGroupRootCausesArgs,
  TicketManagerRootCauseGroupUpdateArgs,
} from './root-causes.api.types';
import {
  normalizeTicketManagerRootCauseGroupResponse,
  normalizeTicketManagerRootCauseGroupsResponse,
  normalizeTicketManagerRootCauseResponse,
  normalizeTicketManagerRootCausesResponse,
} from './root-causes.mappers';
import {
  buildAddRootCauseToGroupRequest,
  buildCreateRootCauseGroupRequest,
  buildCreateRootCauseRequest,
  buildGetProjectRootCauseGroupsRequest,
  buildGetProjectRootCausesRequest,
  buildGetRootCauseGroupRootCausesRequest,
  buildRemoveRootCauseFromGroupRequest,
  buildUpdateRootCauseGroupRequest,
} from './root-causes.routes';

type TicketManagerMutationSuccessResponse = Record<string, never>;

export const RootCauseEndpoints = TicketManagerApi.injectEndpoints({
  endpoints: (builder) => ({
    ticketManagerProjectRootCauseGroups: builder.query<
      ApiResponse<TicketManagerRootCauseGroup[]>,
      TicketManagerProjectRootCauseGroupsArgs
    >({
      query: ({ projectId }) => buildGetProjectRootCauseGroupsRequest({ projectId }),
      transformResponse: normalizeTicketManagerRootCauseGroupsResponse,
      providesTags: (_result, _error, { projectId }) => [
        { type: 'TicketManagerProjectRootCauseGroups', id: projectId },
      ],
    }),

    ticketManagerProjectRootCauses: builder.query<
      ApiResponse<TicketManagerRootCauseReason[]>,
      TicketManagerProjectRootCausesArgs
    >({
      query: ({ projectId }) => buildGetProjectRootCausesRequest({ projectId }),
      transformResponse: normalizeTicketManagerRootCausesResponse,
      providesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectRootCauses', id: projectId }],
    }),

    ticketManagerRootCauseGroupRootCauses: builder.query<
      ApiResponse<TicketManagerRootCauseReason[]>,
      TicketManagerRootCauseGroupRootCausesArgs
    >({
      query: ({ projectId, rootCauseGroupId }) =>
        buildGetRootCauseGroupRootCausesRequest({ projectId, rootCauseGroupId }),
      transformResponse: normalizeTicketManagerRootCausesResponse,
      providesTags: (_result, _error, { rootCauseGroupId }) => [
        { type: 'TicketManagerRootCauseGroupRootCauses', id: rootCauseGroupId },
      ],
    }),

    createTicketManagerRootCauseGroup: builder.mutation<
      ApiResponse<TicketManagerRootCauseGroup>,
      TicketManagerRootCauseGroupCreateArgs
    >({
      query: ({ name, projectId }) => buildCreateRootCauseGroupRequest({ name, projectId }),
      transformResponse: normalizeTicketManagerRootCauseGroupResponse,
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'TicketManagerProjectRootCauseGroups', id: projectId },
      ],
    }),

    updateTicketManagerRootCauseGroup: builder.mutation<
      ApiResponse<TicketManagerRootCauseGroup>,
      TicketManagerRootCauseGroupUpdateArgs
    >({
      query: ({ name, projectId, rootCauseGroupId }) =>
        buildUpdateRootCauseGroupRequest({ name, projectId, rootCauseGroupId }),
      transformResponse: normalizeTicketManagerRootCauseGroupResponse,
      invalidatesTags: (_result, _error, { projectId, rootCauseGroupId }) => [
        { type: 'TicketManagerProjectRootCauseGroups', id: projectId },
        { type: 'TicketManagerRootCauseGroupRootCauses', id: rootCauseGroupId },
      ],
    }),

    createTicketManagerRootCause: builder.mutation<
      ApiResponse<TicketManagerRootCauseReason>,
      TicketManagerRootCauseCreateArgs
    >({
      query: ({ key, name, projectId }) => buildCreateRootCauseRequest({ key, name, projectId }),
      transformResponse: normalizeTicketManagerRootCauseResponse,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectRootCauses', id: projectId }],
    }),

    addTicketManagerRootCauseToGroup: builder.mutation<
      TicketManagerMutationSuccessResponse,
      TicketManagerAddRootCauseToGroupArgs
    >({
      query: ({ projectId, rootCauseGroupId, rootCauseId }) =>
        buildAddRootCauseToGroupRequest({ projectId, rootCauseGroupId, rootCauseId }),
      invalidatesTags: (_result, _error, { projectId, rootCauseGroupId }) => [
        { type: 'TicketManagerProjectRootCauseGroups', id: projectId },
        { type: 'TicketManagerRootCauseGroupRootCauses', id: rootCauseGroupId },
        { type: 'TicketManagerProjectRootCauses', id: projectId },
      ],
    }),

    removeTicketManagerRootCauseFromGroup: builder.mutation<
      TicketManagerMutationSuccessResponse,
      TicketManagerRemoveRootCauseFromGroupArgs
    >({
      query: ({ projectId, rootCauseGroupId, rootCauseId }) =>
        buildRemoveRootCauseFromGroupRequest({ projectId, rootCauseGroupId, rootCauseId }),
      invalidatesTags: (_result, _error, { projectId, rootCauseGroupId }) => [
        { type: 'TicketManagerProjectRootCauseGroups', id: projectId },
        { type: 'TicketManagerRootCauseGroupRootCauses', id: rootCauseGroupId },
      ],
    }),
  }),
});

export const {
  useTicketManagerProjectRootCauseGroupsQuery,
  useTicketManagerProjectRootCausesQuery,
  useTicketManagerRootCauseGroupRootCausesQuery,
  useCreateTicketManagerRootCauseGroupMutation,
  useUpdateTicketManagerRootCauseGroupMutation,
  useCreateTicketManagerRootCauseMutation,
  useAddTicketManagerRootCauseToGroupMutation,
  useRemoveTicketManagerRootCauseFromGroupMutation,
} = RootCauseEndpoints;
